import featureFlagModule, { FeatureFlagKeys } from '@modules/FeatureFlagModule';
import getUsers from '../api/users/getUsers';
import ServiceUser from '../types/ServiceUser';
import refreshUser from './refreshUser';
import { serviceState } from './state';
import { logException } from './log';

export function getUser(userId: string): ServiceUser | null {
  return serviceState.userById[userId] || null;
}

export function getUserByContactCenterUserId(
  contactCenterUserId: string,
): ServiceUser | null {
  return serviceState.userByContactCenterUserId[contactCenterUserId] || null;
}

async function refreshUsers(): Promise<void> {
  const freshUsers = await getUsers(serviceState.id ?? '');
  serviceState.users = freshUsers.map((u) => {
    const existing = u.contactCenterUserId
      ? getUserByContactCenterUserId(u.contactCenterUserId)
      : null;
    if (!existing) return u;
    return {
      ...u,
      stats: u.stats ?? existing.stats,
      activityStatus: existing.activityStatus,
      availabilityStatus: existing.availabilityStatus,
    };
  });
}

async function requireUser(userId: string): Promise<ServiceUser> {
  let user = getUserByContactCenterUserId(userId);
  if (!user) {
    const singleItemEnabled = featureFlagModule.isEnabled(
      FeatureFlagKeys.featureRtcsSingleItemFetch,
    );

    // Single-item fetch first when flag-on; isolate its failure so the
    // list-refresh fallback below still runs when it throws (not just nulls).
    if (singleItemEnabled) {
      try {
        const fetched = await refreshUser(userId);
        if (fetched) {
          user = getUserByContactCenterUserId(userId);
        }
      } catch (err) {
        logException(err, 'rtcs:user:refresh:single-item-failed');
      }
    }

    // Last-resort list refetch when single-item is disabled or did not surface the user.
    if (!user) {
      try {
        await refreshUsers();
      } catch (err) {
        logException(err, 'Failed to refresh users');
      }
      user = getUserByContactCenterUserId(userId);
    }
  }
  if (!user) {
    console.error(
      `A websocket event did not find a valid user account:: ${userId}, try refreshing your browser.`,
    );
    throw new Error();
  }
  return user;
}

export default requireUser;
